import React from 'react';
import { EmptyTableState, TableLoadingSkeleton, TableErrorState } from './EmptyTableState';

// Simple drop-in replacement for your existing empty state using the generic component
export const TenantsEmptyState: React.FC<{
  hasFilters?: boolean;
  onCreateTenant?: () => void;
  onClearFilters?: () => void;
  showImportOption?: boolean;
}> = ({ hasFilters = false, onCreateTenant, onClearFilters, showImportOption = true }) => {
  return (
    <EmptyTableState
      title="No tenants found"
      description="Get started by adding your first tenant to the platform. Tenants represent your customers or organizations using your SaaS application."
      hasFilters={hasFilters}
      onClearFilters={onClearFilters}
      primaryAction={onCreateTenant ? {
        label: "Add Your First Tenant",
        onClick: onCreateTenant,
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        )
      } : undefined}
      secondaryAction={showImportOption ? {
        label: "Import from CSV",
        onClick: () => console.log("Import CSV clicked"), // You can implement this later
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
        )
      } : undefined}
    />
  );
};

// Loading skeleton for the table - now uses the generic component
export const TenantsLoadingSkeleton: React.FC = () => <TableLoadingSkeleton rows={5} columns={9} />;

// Error state for when API calls fail - now uses the generic component
export const TenantsErrorState: React.FC<{
  onRetry?: () => void;
  error?: string;
}> = ({ onRetry, error }) => (
  <TableErrorState 
    onRetry={onRetry}
    error={error || "Failed to load tenants"}
    title="Failed to load tenants"
  />
);

// Usage examples for different scenarios:

// Example 1: Basic usage in your tenants.tsx
export const ExampleUsageInTenantsPage = () => {
  const tenants = []; // Your tenants array
  const hasActiveFilters = false; // Check if any filters are applied
  const isLoading = false; // Your loading state
  const error = null; // Your error state

  if (isLoading) {
    return <TenantsLoadingSkeleton />;
  }

  if (error) {
    return <TenantsErrorState onRetry={() => {/* your retry logic */}} error={error} />;
  }

  if (tenants.length === 0) {
    return (
      <div className="card">
        <TenantsEmptyState 
          hasFilters={hasActiveFilters}
          onCreateTenant={() => {/* your create tenant logic */}}
          onClearFilters={() => {/* your clear filters logic */}}
        />
      </div>
    );
  }

  // Your existing table code here
  return null;
};

// Example 2: Integration with your existing logic
export const TenantsPageEmptyStateIntegration = `
// In your tenants.tsx file, replace this:

{tenants.length === 0 ? (
  <div className="text-center py-12">
    <p className="text-gray-500 text-lg mb-4">No tenants found</p>
    <p className="text-gray-400 text-sm">Click "Add New Tenant" to create your first tenant</p>
  </div>
) : (
  // your table
)}

// With this:

{tenants.length === 0 ? (
  <TenantsEmptyState 
    hasFilters={Object.keys(filters).some(key => filters[key] !== undefined && filters[key] !== '')}
    onCreateTenant={() => setIsCreateModalOpen(true)}
    onClearFilters={handleClearFilters}
  />
) : (
  // your table
)}
`;
